import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Loader2, Plus, Cake, BellRing, Check,
  Pencil, Trash2,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { usePeople } from '../../hooks/usePeople'
import { cn } from '../../lib/utils'

// /people — light personal CRM. The handful of people that matter:
// birthdays, contact cadence, "gone quiet" nudges, one-tap "contacted
// now". Sorted needs-attention-first by the hook.

export function PeoplePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { people, loading, save, remove, logContact } = usePeople()
  const [editing, setEditing] = useState(null) // null | 'new' | person
  const [busy, setBusy] = useState(false)

  const submit = async (values) => {
    setBusy(true)
    const ok = await save(values, editing === 'new' ? null : editing.id)
    setBusy(false)
    if (ok) setEditing(null)
  }

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('people.title')}
        </h1>
        {!editing && (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="w-4 h-4 me-1" />
            {t('people.add')}
          </Button>
        )}
      </div>

      {editing && (
        <PersonForm
          person={editing === 'new' ? null : editing}
          onSave={submit}
          onCancel={() => setEditing(null)}
          busy={busy}
          t={t}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {!loading && people.length === 0 && !editing && (
        <p className="text-sm text-muted-foreground text-center py-8" dir="auto">{t('people.empty')}</p>
      )}

      {!loading && people.length > 0 && (
        <Card>
          <CardContent className="p-0 divide-y divide-border/60">
            {people.map((p) => (
              <PersonRow
                key={p.id}
                person={p}
                onContacted={() => logContact(p.id)}
                onEdit={() => setEditing(p)}
                onDelete={() => remove(p.id)}
                t={t}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function PersonRow({ person, onContacted, onEdit, onDelete, t }) {
  const [confirming, setConfirming] = useState(false)
  const [logging, setLogging] = useState(false)

  const contacted = async () => {
    if (logging) return
    setLogging(true)
    await onContacted()
    setLogging(false)
  }

  return (
    <div className="p-3 space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate" dir="auto">
            {person.name}
            {person.relation && (
              <span className="font-normal text-muted-foreground"> · {person.relation}</span>
            )}
          </p>
          {person.last_contact && (
            <p className="text-[11px] text-muted-foreground" dir="auto">
              {t('people.lastContact')} <span dir="ltr">{person.last_contact}</span>
            </p>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={contacted} disabled={logging}>
          {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 me-1" />}
          {!logging && t('people.contacted')}
        </Button>
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('common.edit')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {confirming ? (
          <Button size="sm" variant="destructive" onClick={onDelete} onBlur={() => setConfirming(false)}>
            {t('people.confirm')}
          </Button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label={t('common.delete')}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {(person.overdueDays != null || (person.birthdayIn != null && person.birthdayIn <= 14)) && (
        <div className="flex flex-wrap gap-1.5">
          {person.overdueDays != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[11px] font-medium" dir="auto">
              <BellRing className="w-3 h-3" />
              {t('people.quiet', { count: person.overdueDays })}
            </span>
          )}
          {person.birthdayIn != null && person.birthdayIn <= 14 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-app-primary-500/10 text-app-primary-500 text-[11px] font-medium" dir="auto">
              <Cake className="w-3 h-3" />
              {person.birthdayIn === 0 ? t('people.birthdayToday') : t('people.birthdayIn', { count: person.birthdayIn })}
            </span>
          )}
        </div>
      )}

      {person.notes && (
        <p className="text-xs text-muted-foreground whitespace-pre-wrap" dir="auto">{person.notes}</p>
      )}
    </div>
  )
}

function PersonForm({ person, onSave, onCancel, busy, t }) {
  const [name, setName] = useState(person?.name || '')
  const [relation, setRelation] = useState(person?.relation || '')
  const [birthday, setBirthday] = useState(person?.birthday || '')
  const [cadence, setCadence] = useState(person?.contact_every_days || '')
  const [notes, setNotes] = useState(person?.notes || '')

  const submit = (e) => {
    e.preventDefault()
    if (!name.trim()) return
    onSave({ name, relation, birthday, contact_every_days: cadence, notes })
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <form onSubmit={submit} className="space-y-2">
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={t('people.namePlaceholder')} maxLength={80}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" dir="auto"
          />
          <input
            type="text" value={relation} onChange={(e) => setRelation(e.target.value)}
            placeholder={t('people.relationPlaceholder')} maxLength={60}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" dir="auto"
          />
          <div className="flex gap-2">
            <label className="flex-1 space-y-1">
              <span className="text-[11px] text-muted-foreground" dir="auto">{t('people.birthday')}</span>
              <input
                type="date" value={birthday || ''} onChange={(e) => setBirthday(e.target.value)}
                className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
              />
            </label>
            <label className="flex-1 space-y-1">
              <span className="text-[11px] text-muted-foreground" dir="auto">{t('people.cadence')}</span>
              <input
                type="number" min="1" max="365" value={cadence}
                onChange={(e) => setCadence(e.target.value)}
                placeholder="30"
                className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" dir="ltr"
              />
            </label>
          </div>
          <textarea
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={t('people.notesPlaceholder')} rows={2} maxLength={500}
            className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none" dir="auto"
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !name.trim()} className="flex-1">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default PeoplePage
